/**
 * A banner as a program's picture (banner studio, owner 2026-10-31: "attach banner to program instead of downloading
 * and uploading").
 *
 * The panel draws the banner at the screens' size (lib/social drawScreenImage, 1920×1080) and sends it through the
 * event's own picture route — the same pipe as an upload — naming the banner it came from (`bannerId`), so the app,
 * the screens and the website show it with nothing new to learn, and the program can say «Fra banneret …» and be
 * redrawn when the banner changes. Nothing is stored in the browser.
 */
import { send, type Body } from './api';
import { drawScreenImage, mosquePublic, type ShareSource } from './social';

export type BannerSource = Extract<ShareSource, { kind: 'banner' }>;
/** A banner row (GET /admin/banners items, or the studio's own state) as what the drawing needs. */
export function sourceOf(b: Body, photoSrc: string | null = null): BannerSource {
  return { kind: 'banner', id: b.id, template: b.template, palette: b.palette, layers: (b.layers || {}) as Record<string, string>,
    picture: b.template === 'image', decor: b.decor || null, photo: b.template === 'photo', photoSrc };
}

const asDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onerror = () => reject(r.error);
  r.onload = () => resolve(String(r.result || ''));
  r.readAsDataURL(blob);
});

/** The banner drawn for a program: a JPEG data: URL at 1920×1080, or null when the browser cannot draw it. */
export async function drawForEvent(b: Body, photoSrc: string | null = null): Promise<string | null> {
  const m = await mosquePublic();
  const blob = await drawScreenImage(sourceOf(b, photoSrc), m.shortName, 'image/jpeg');
  return blob ? asDataUrl(blob) : null;
}

/** Draws the banner and makes it the event's picture. → null when done, or what went wrong, in Norwegian. */
export async function attachToEvent(eventId: string, b: Body, photoSrc: string | null = null): Promise<string | null> {
  const data = await drawForEvent(b, photoSrc);
  if (!data) return 'Bildet kunne ikke lages i denne nettleseren.';
  const r = await send(`/events/${encodeURIComponent(eventId)}/image`, { method: 'PUT', body: JSON.stringify({ data, bannerId: b.id }) });
  return r.ok ? null : (r.body.message_no || r.body.message || 'Bildet ble ikke lagret.');
}

/** The title a banner is called by in the panel. */
export const bannerTitle = (b: Body): string => (b && b.layers && (b.layers.headline || b.layers.eyebrow)) || '(uten overskrift)';
